/**
 * Icon Service
 * Handles icon search requests via the backend API (Iconify proxy)
 */

import axios from 'axios';
import { getAuthToken } from './firebase';

// Configure axios for icon API
const getApiBaseUrl = () => {
  // @ts-ignore - window.__ENV__ is injected at runtime by Docker entrypoint
  const runtimeApiUrl = (window as any).__ENV__?.API_URL;
  const buildTimeApiUrl = import.meta.env.VITE_API_URL;

  if (runtimeApiUrl) {
    return `${runtimeApiUrl}/api`;
  }
  if (buildTimeApiUrl && buildTimeApiUrl !== '/api') {
    return `${buildTimeApiUrl}/api`;
  }
  return '/api';
};

const iconApi = axios.create({
  timeout: 30000, // 30 seconds for icon searches
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth
iconApi.interceptors.request.use(
  async (config) => {
    config.baseURL = getApiBaseUrl();

    const token = await getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Icon result from the search API
 */
export interface IconResult {
  id: string;           // e.g., "lucide:home"
  name: string;         // e.g., "home"
  collection: string;   // e.g., "lucide"
  collectionName: string; // e.g., "Lucide Icons"
  svgUrl: string;       // URL with default color/size
  svgUrlBase: string;   // Base URL for customization
}

/**
 * Icon search response
 */
export interface IconSearchResponse {
  success: boolean;
  query: string;
  totalMatches: number;
  icons: IconResult[];
  error?: string;
}

/**
 * Icon search options
 */
export interface IconSearchOptions {
  style?: 'line' | 'solid' | 'all';
  limit?: number;
  color?: string;  // hex without #
  size?: number;   // pixels
}

/**
 * Icon collection info
 */
export interface IconCollection {
  id: string;
  name: string;
  style: 'line' | 'solid';
}

const ICONIFY_API = 'https://api.iconify.design';

/**
 * Icon Service class
 */
class IconService {
  /**
   * Search for icons
   */
  async searchIcons(
    query: string,
    options: IconSearchOptions = {}
  ): Promise<IconSearchResponse> {
    const {
      style = 'line',
      limit = 20,
      color = '000000',
      size = 64
    } = options;

    try {
      const response = await iconApi.post('/icons/search', {
        query,
        style,
        limit,
        color,
        size
      });
      return response.data;
    } catch (error) {
      console.error('Icon search error:', error);
      return {
        success: false,
        query,
        totalMatches: 0,
        icons: [],
        error: error instanceof Error ? error.message : 'Icon search failed'
      };
    }
  }

  /**
   * Get popular/curated icons for empty state
   */
  async getPopularIcons(
    style: 'line' | 'solid' | 'all' = 'line',
    color: string = '000000',
    size: number = 64
  ): Promise<IconSearchResponse> {
    try {
      const response = await iconApi.get('/icons/popular', {
        params: { style, color, size }
      });
      return response.data;
    } catch (error) {
      console.error('Popular icons error:', error);
      return {
        success: false,
        query: 'popular',
        totalMatches: 0,
        icons: [],
        error: error instanceof Error ? error.message : 'Failed to fetch popular icons'
      };
    }
  }

  /**
   * Get available icon collections
   */
  async getCollections(): Promise<{ success: boolean; collections: IconCollection[] }> {
    try {
      const response = await iconApi.get('/icons/collections');
      return response.data;
    } catch (error) {
      console.error('Collections error:', error);
      return {
        success: false,
        collections: []
      };
    }
  }

  /**
   * Build a customized icon URL with specific color and size
   * This allows real-time preview customization without additional API calls
   */
  getIconUrl(iconId: string, color: string = 'ffffff', size: number = 64): string {
    const [collection, name] = iconId.split(':');
    if (!collection || !name) {
      console.warn('Invalid icon ID:', iconId);
      return '';
    }
    return `${ICONIFY_API}/${collection}/${name}.svg?color=%23${color}&height=${size}`;
  }

  /**
   * Get the base SVG URL without color/size parameters
   */
  getIconBaseUrl(iconId: string): string {
    const [collection, name] = iconId.split(':');
    if (!collection || !name) {
      console.warn('Invalid icon ID:', iconId);
      return '';
    }
    return `${ICONIFY_API}/${collection}/${name}.svg`;
  }

  /**
   * Parse icon ID from an Iconify URL
   * Returns null if URL is not a valid Iconify URL
   */
  parseIconFromUrl(url: string): { iconId: string; color: string; size: number } | null {
    if (!url.includes('api.iconify.design')) {
      return null;
    }

    try {
      // Extract icon ID from path: /collection/name.svg
      const match = url.match(/iconify\.design\/([^/]+)\/([^.]+)\.svg/);
      if (!match) return null;

      const iconId = `${match[1]}:${match[2]}`;

      // Extract color if present
      const colorMatch = url.match(/color=%23([0-9a-fA-F]+)/);
      const color = colorMatch ? colorMatch[1] : 'ffffff';

      // Extract size if present
      const sizeMatch = url.match(/height=(\d+)/);
      const size = sizeMatch ? parseInt(sizeMatch[1], 10) : 64;

      return { iconId, color, size };
    } catch {
      return null;
    }
  }

  /**
   * Check if a URL is an Iconify icon URL
   */
  isIconifyUrl(url: string): boolean {
    return url.includes('api.iconify.design');
  }
}

export const iconService = new IconService();
